/**
 * Relationship story UI (ENGAGEMENT_PLAN §5.2 F4/F5) — wired to the server-side
 * bio and history endpoints.
 *
 * Everything rendered here is fixed product copy: verdicts and signals pick
 * keys, the text is hand-written (guardrail 1). `bio.emphasis` and the Jev
 * drift verdict are quarantined by calibration, so the card uses default copy
 * order and the history drift chapter is the deterministic one.
 */

import { useMemo, type ComponentType } from 'react';
import {
  BookOpen,
  CalendarDays,
  Camera,
  Clock,
  Flame,
  Gem,
  Handshake,
  Heart,
  History,
  Home,
  Info,
  MessageCircle,
  Moon,
  Phone,
  Repeat,
  ScrollText,
  Sparkles,
  Sprout,
  Users,
  Waves,
  Wrench,
} from 'lucide-react';
import { RELATIONSHIP_METRICS_COPY, THEME_META, TIER_LABELS, type RelationshipTier } from '@toiyp/shared';
import type { AvatarView } from '../profiles/api';
import { Avatar } from '../profiles/avatar';
import type { BioView, HistoryView } from './api';
import { useBioQuery, useHistoryQuery } from './api';
import { formatStamp, formatFullStamp } from '../letters/ui';

/* ── The list order — one ordering only: newest first (F3 as shipped) ───── */

/** Stable newest-first sort — last letter, then relationship order. */
export function sortNewestFirst<T extends { last: { sealedAt: string | null } | null }>(items: T[]): T[] {
  return [...items].sort((a, b) => (b.last?.sealedAt ?? '').localeCompare(a.last?.sealedAt ?? ''));
}

/* ── The bio card (F4) ───────────────────────────────────────────────────── */

const LOCKED_COPY: Record<string, string> = {
  contact: 'Contact details',
  photos: 'Photo attachments',
  history: 'Relationship history',
};

/**
 * Each locked capability gets its own section and its own line (2026-02-10) —
 * no repeated "…shared once your letters have earned some weight" suffix. The
 * notes are written like a friend advising, not a feature gate.
 */
const CAP_LOCKED: Record<string, { title: string; note: string }> = {
  contact: {
    title: 'Contact details',
    note: "Hang fire on sharing numbers until you've got to know each other better.",
  },
  photos: {
    title: 'Photographs',
    note: 'Cultivate your relationship further, and photo sharing opens up.',
  },
  history: {
    title: 'The history',
    note: "Once your letters have run a while, the whole story back to the first one opens.",
  },
};

/** Each capability earns its own icon — status by symbol, not just words. */
const CAP_ICON: Record<string, ComponentType<{ size?: number; className?: string }>> = {
  contact: Phone,
  photos: Camera,
  history: ScrollText,
};

/** A letter theme gets its own symbol — subjects by icon, not bare words. */
const THEME_ICON: Record<string, ComponentType<{ size?: number }>> = {
  romantic: Heart,
  family: Home,
  'old-friend': Users,
  spiritual: Gem,
  'shared-craft': Wrench,
  everyday: MessageCircle,
};

/** The relationship status as symbol: both avatars plus the tier's mark. */
const TIER_MARK: Record<RelationshipTier, ComponentType<{ size?: number }>> = {
  cold: Moon,
  new: Sprout,
  tepid: Waves,
  hot: Flame,
  trusted: Handshake,
};

function PairMark({
  mine,
  peer,
  tier,
}: {
  mine: AvatarView | null;
  peer: AvatarView | null;
  tier: RelationshipTier;
}) {
  const Mark = TIER_MARK[tier];
  return (
    <span className="pair-mark" role="img" aria-label={`This friendship is ${TIER_LABELS[tier]}`}>
      <Avatar name="you" avatar={mine} />
      <Avatar name="them" avatar={peer} />
      <span className={`pair-mark__status pair-mark__status--${tier}`} aria-hidden="true">
        <Mark size={13} />
      </span>
    </span>
  );
}

/** The dominant tone, as fixed copy ("lately: warm" / "mostly matter-of-fact"). */
function toneSummary(tone?: { warm: number; neutral: number; tense: number }): string | null {
  if (!tone) return null;
  const total = tone.warm + tone.neutral + tone.tense;
  if (total === 0) return null;
  const pick =
    tone.warm > tone.neutral && tone.warm > tone.tense
      ? 'warm'
      : tone.tense > tone.warm && tone.tense > tone.neutral
        ? 'strained'
        : 'matter-of-fact';
  return pick === 'matter-of-fact' ? 'mostly matter-of-fact' : `lately: ${pick}`;
}

export function BioCard({
  relationshipId,
  peerName,
  mineAvatar,
  peerAvatar,
  showLocks = true,
}: {
  relationshipId: string;
  peerName: string;
  mineAvatar: AvatarView | null;
  peerAvatar: AvatarView | null;
  /** The locked-capability sections; off where the tier ladder says it instead. */
  showLocks?: boolean;
}) {
  const { data: bio, isFetching, isError, refetch } = useBioQuery({ relationshipId });

  const band = useMemo(() => {
    if (!bio?.engagementBand) return null;
    const copy = RELATIONSHIP_METRICS_COPY['engagement.band'][bio.engagementBand];
    return copy ?? bio.engagementBand;
  }, [bio]);

  if (!bio && isFetching) {
    return (
      <div className="story-card story-card--loading" role="status">
        Looking back through your letters…
      </div>
    );
  }
  if (!bio && isError) {
    return (
      <div className="story-card story-card--error" role="alert">
        The letters didn't load — try again.
        <button type="button" className="btn btn--ghost" onClick={() => void refetch()}>
          Try again
        </button>
      </div>
    );
  }
  if (!bio) return null;

  const honesty =
    bio.oneSided === true
      ? bio.depth !== 'basic' && bio.perDirection
        ? `You've written ${bio.perDirection.mine}, ${peerName} has written ${bio.perDirection.theirs}.`
        : 'Most of the letters have been from one side.'
      : null;

  // One compact meta: each fact carries its icon, no dash-joined stats line.
  const meta: { Icon: ComponentType<{ size?: number }>; text: string }[] = [];
  if (bio.letterCount > 0) {
    meta.push({ Icon: BookOpen, text: `${bio.letterCount} letter${bio.letterCount === 1 ? '' : 's'}` });
  }
  if (bio.completedExchanges > 0) {
    meta.push({
      Icon: Repeat,
      text: `${bio.completedExchanges} full back-and-forth${bio.completedExchanges === 1 ? '' : 's'}`,
    });
  }
  if (bio.depth !== 'basic' && bio.responseWindowHours != null) {
    meta.push({ Icon: Clock, text: `replies usually within ~${Math.round(bio.responseWindowHours)} h` });
  }
  const tone = toneSummary(bio.toneCounts);
  if (tone) meta.push({ Icon: Sparkles, text: tone });

  return (
    <div className="bio-card" role="region" aria-label="Your letters together">
      <div className="bio-card__head">
        <PairMark mine={mineAvatar} peer={peerAvatar} tier={bio.tier} />
        <div className="bio-card__headbox">
          <span className="bio-card__title">Your letters together</span>
          {band && <span className={`pill pill--tier-${bio.engagementBand}`}>{band}</span>}
        </div>
      </div>

      {meta.length > 0 && (
        <div className="bio-card__meta">
          {meta.map((m, i) => (
            <span key={i} className="bio-card__metaitem">
              <m.Icon size={12} aria-hidden="true" />
              {m.text}
            </span>
          ))}
        </div>
      )}
      {(bio.themes ?? []).length > 0 && (
        <div className="bio-card__themes">
          {(bio.themes ?? []).map((t) => {
            const Icon = THEME_ICON[t.theme] ?? Info;
            const label = THEME_META[t.theme as keyof typeof THEME_META]?.label ?? t.theme;
            return (
              <span key={t.theme} className="bio-card__theme">
                <Icon size={12} aria-hidden="true" />
                {label}
              </span>
            );
          })}
        </div>
      )}
      {bio.lastLetterAt && (
        <p className="bio-card__line">
          <CalendarDays size={12} aria-hidden="true" /> last letter {formatStamp(bio.lastLetterAt)}
        </p>
      )}

      {honesty && <p className="bio-card__line bio-card__honesty">{honesty}</p>}
      {bio.rekindle && (
        <p className="bio-card__line bio-card__rekindle">
          It&apos;s been a while — a short letter would restart this.
        </p>
      )}

      {showLocks && bio.lockedCapabilities.length > 0 && (
        <div className="bio-card__locks" aria-label="What comes as your letters go on">
          {bio.lockedCapabilities.map((cap) => {
            const Icon = CAP_ICON[cap] ?? Info;
            const locked = CAP_LOCKED[cap] ?? { title: LOCKED_COPY[cap] ?? cap, note: '' };
            return (
              <div key={cap} className="bio-card__lock">
                <span className="bio-card__lock-icon">
                  <Icon size={15} aria-hidden="true" />
                </span>
                <span className="bio-card__lock-body">
                  <span className="bio-card__lock-title">{locked.title}</span>
                  {locked.note && <span className="bio-card__lock-note">{locked.note}</span>}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* ── The tier ladder — what opens as the letters go on ─────────────────── */

/** The rungs, in order. `cold` is a lapse, not a rung: it sits below the first. */
const LADDER: RelationshipTier[] = ['new', 'tepid', 'hot', 'trusted'];
const LADDER_CAPS = ['contact', 'photos', 'history'] as const;

/**
 * One compact ladder in place of a section per locked capability: the tiers
 * as steps (where you are, what's behind you), and what the top rung opens,
 * folded behind a summary line. The tier's reason is the current step's hint.
 */
export function TierLadder({
  tier,
  reason,
  capabilities,
}: {
  tier: RelationshipTier;
  /** Fixed-copy explanation of the current tier, if any. */
  reason: string | null;
  capabilities: string[];
}) {
  const at = LADDER.indexOf(tier);
  const open = LADDER_CAPS.filter((c) => capabilities.includes(c));
  const allOpen = open.length === LADDER_CAPS.length;
  return (
    <section className="ladder" aria-label="What opens as you write">
      <div className="ladder__head">
        <span className="ladder__title">{allOpen ? 'Opened by your letters' : 'Unlocks as you write'}</span>
      </div>
      <ol className="ladder__steps">
        {LADDER.map((t, i) => {
          const Mark = TIER_MARK[t];
          const state = i < at ? 'done' : i === at ? 'now' : 'next';
          return (
            <li
              key={t}
              className={`ladder__step ladder__step--${state}`}
              aria-current={state === 'now' ? 'step' : undefined}
              title={state === 'now' && reason ? reason : TIER_LABELS[t]}
            >
              <span className="ladder__dot" aria-hidden="true">
                <Mark size={13} />
              </span>
              <span className="ladder__label">{TIER_LABELS[t]}</span>
            </li>
          );
        })}
      </ol>
      {tier === 'cold' && reason && <p className="ladder__note">{reason}</p>}
      <details className="ladder__unlocks">
        <summary>
          <span className="ladder__caps" aria-hidden="true">
            {LADDER_CAPS.map((c) => {
              const Icon = CAP_ICON[c] ?? Info;
              return (
                <span
                  key={c}
                  className={`ladder__cap${capabilities.includes(c) ? ' ladder__cap--open' : ''}`}
                >
                  <Icon size={12} />
                </span>
              );
            })}
          </span>
          <span className="ladder__summary">
            {allOpen
              ? 'Contact details, photos and the history are open'
              : `Contact details, photos and the history open at ${TIER_LABELS.trusted}`}
          </span>
        </summary>
        <ul className="ladder__list">
          {LADDER_CAPS.map((c) => {
            const Icon = CAP_ICON[c] ?? Info;
            const copy = CAP_LOCKED[c]!;
            const isOpen = capabilities.includes(c);
            return (
              <li key={c} className={`ladder__item${isOpen ? ' ladder__item--open' : ''}`}>
                <Icon size={13} aria-hidden="true" />
                <span>
                  <strong>{copy.title}</strong>
                  {isOpen ? ' — open between you.' : ` — ${copy.note}`}
                </span>
              </li>
            );
          })}
        </ul>
      </details>
    </section>
  );
}

/* ── The history timeline (F5) — rendered only with the `history` capability ─ */

const MILESTONE_SUFFIX: Record<number, string> = { 10: 'th', 25: 'th', 50: 'th', 100: 'th' };
const TONE_CSS: Record<string, string> = {
  warm: 'story-spark__dot--warm',
  neutral: 'story-spark__dot--neutral',
  tense: 'story-spark__dot--tense',
};

/** A tiny, accessible tone sparkline — dots are letters, colour is the tone. */
function ToneSpark({ series }: { series: HistoryView['toneSeries'] }) {
  const capped = series.slice(-64);
  const counts = useMemo(() => {
    const c = { warm: 0, neutral: 0, tense: 0 };
    for (const s of capped) c[s.tone]++;
    return c;
  }, [capped]);
  return (
    <svg
      className="story-spark"
      width={Math.max(capped.length * 5 - 1, 4)}
      height={12}
      role="img"
      aria-label={`Tone over the last ${capped.length} letters — ${counts.warm} warm, ${counts.neutral} neutral, ${counts.tense} tense.`}
    >
      {capped.map((s, i) => (
        <circle
          key={i}
          className={`story-spark__dot ${TONE_CSS[s.tone] ?? 'story-spark__dot--neutral'}`}
          cx={i * 5 + 1}
          cy={6}
          r={2}
        />
      ))}
    </svg>
  );
}

export function HistoryCard({
  relationshipId,
  enabled,
}: {
  relationshipId: string;
  enabled: boolean;
}) {
  const { data: history, isFetching, isError, refetch } = useHistoryQuery(
    { relationshipId },
    { skip: !enabled },
  );
  if (!enabled) return null;
  if (!history && isFetching) {
    return (
      <div className="story-card story-card--loading" role="status">
        Looking back through your letters…
      </div>
    );
  }
  if (!history && isError) {
    return (
      <div className="story-card story-card--error" role="alert">
        Could not read the history of this correspondence.
        <button type="button" className="btn btn--ghost" onClick={() => void refetch()}>
          Try again
        </button>
      </div>
    );
  }
  if (!history) return null;

  const driftCopy = history.drift
    ? RELATIONSHIP_METRICS_COPY[history.drift === 'warmer' ? 'tone.drift.warmer' : 'tone.drift.cooler']
    : null;

  return (
    <div className="history-card" role="region" aria-label="The history of this correspondence">
      <div className="history-card__head">
        <BookOpen size={13} aria-hidden="true" />
        <span className="history-card__title">The history</span>
        <span className="pill pill--tier-hot">{TIER_LABELS.trusted}</span>
      </div>

      <p className="history-card__meta">
        {history.firstLetterAt && <>First letter {formatFullStamp(history.firstLetterAt)}</>}
        {history.firstLetterAt && ' · '}
        {history.letterCount} letter{history.letterCount === 1 ? '' : 's'} · {history.completedExchanges} full back-and-forth
        {history.completedExchanges === 1 ? '' : 's'}
        {history.longestSilenceDays != null && ` · quietest stretch ${history.longestSilenceDays} days`}
      </p>

      {history.milestones.length > 0 && (
        <ul className="history-card__list" aria-label="Letter anniversaries">
          {history.milestones.map((m) => (
            <li key={m.letterNumber} className="history-card__row">
              <Sparkles size={11} aria-hidden="true" />
              <strong>{m.letterNumber}{MILESTONE_SUFFIX[m.letterNumber] ?? 'th'} letter</strong>
              {m.sealedAt && <span>{formatStamp(m.sealedAt)}</span>}
            </li>
          ))}
        </ul>
      )}

      {humane(history.tierHistory).length > 1 && (
        <ul className="history-card__list" aria-label="How the relationship grew">
          {humane(history.tierHistory).map((t) => (
            <li key={`${t.tier}-${t.changedAt}`} className="history-card__row" title={t.why}>
              <History size={11} aria-hidden="true" />
              <strong className={`pill pill--tier-${t.tier}`}>{TIER_LABELS[t.tier]}</strong>
              <span>{formatStamp(t.changedAt)}</span>
            </li>
          ))}
        </ul>
      )}

      {history.toneSeries.length > 0 && (
        <div className="history-card__spark">
          <span className="history-card__spark-label">
            <Clock size={11} aria-hidden="true" /> tone
          </span>
          <ToneSpark series={history.toneSeries} />
        </div>
      )}
      {driftCopy && <p className="bio-card__line">{driftCopy}</p>}
    </div>
  );
}

function humane(history: HistoryView['tierHistory']) {
  return history.map((h) => ({
    ...h,
    why: (h.reasons ?? []).join(' '),
  }));
}